import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';

import { AppState } from '../../store/states/app.state';
import { UserState } from '../../store/states/user.state';
import { StoreName } from '../../store/definitions/store.constants';
import { UserType } from '../../permissions/definitions/permissions.enums';
import { StorageService } from '../../services/storage/storage.service';
import { Module } from '../../models/user/user.model';
import { FINANCIAL_HUB_SECTIONS, FINANCIAL_HUB_FALLBACK_PATH } from '../financial-hub.constants';

/**
 * Index (empty-path) redirect guard for Financial Hub. A static `redirectTo` is resolved during
 * route recognition — before any guard runs — so a fixed `redirectTo: 'purchase-orders'` always
 * lands the user in the PO subtree, where the global PermissionGuard then denies anyone without
 * the `financial-hub/purchase-orders` slug and bounces them to /account. This guard instead picks
 * the first section the user can actually access (see FINANCIAL_HUB_SECTIONS) at navigation time.
 *
 * It always navigates and returns false (the empty route renders nothing of its own). Admins and
 * users with every section fall through to the first entry (purchase-orders), preserving the
 * previous default landing.
 */
@Injectable()
export class FinancialHubDefaultSectionGuard implements CanActivate {
  constructor(
    private readonly store: Store<AppState>,
    private readonly router: Router,
    private readonly storageService: StorageService,
  ) {}

  canActivate(): Observable<boolean> {
    return this.store.pipe(
      select(StoreName.user),
      take(1),
      map((userState: UserState) => {
        const user: UserState = Object.keys(userState).length
          ? userState
          : this.storageService.getUserAction() as unknown as UserState;

        const isAdmin = user.user_type === UserType.admin;
        const modules = user.modules || [];

        const target = FINANCIAL_HUB_SECTIONS.find(
          (section) => isAdmin || this.hasModuleAccess(modules, section.module),
        );

        this.router.navigate([target ? target.path : FINANCIAL_HUB_FALLBACK_PATH]);
        return false;
      }),
    );
  }

  // Prefix match: the user holds `modulePrefix` itself or any descendant slug under it.
  private hasModuleAccess(modules: Module[], modulePrefix: string): boolean {
    return modules.some(
      (module: Module) => module.slug === modulePrefix || module.slug.startsWith(`${modulePrefix}/`),
    );
  }
}
